import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";

import { formatCents, formatCompetence } from "../../utils/directorFormat";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const POOL_COLORS = ["#7455FB", "#00AFC7", "#FD7A53", "#35516F", "#9B6B43"];

const CashRunwayTable = ({ months }) => <div className="director-table-scroll">
  <table className="director-table" aria-label="Alternativa tabular da pista de caixa">
    <thead>
      <tr>
        <th scope="col">Competência</th>
        <th scope="col">Receita realizada</th>
        <th scope="col">Receita prevista</th>
        <th scope="col">Saída realizada</th>
        <th scope="col">Saída comprometida</th>
        <th scope="col">Saída planejada</th>
        <th scope="col">Fluxo líquido</th>
        <th scope="col">Saldo</th>
      </tr>
    </thead>
    <tbody>
      {months.map((month) => <tr key={month.competence}>
        <th scope="row">{formatCompetence(month.competence)}</th>
        <td>{formatCents(month.actualInflowCents)}</td>
        <td>{formatCents(month.forecastInflowCents)}</td>
        <td>{formatCents(month.actualOutflowCents)}</td>
        <td>{formatCents(month.committedOutflowCents)}</td>
        <td>{formatCents(month.plannedUncommittedOutflowCents)}</td>
        <td>{formatCents(month.netFlowCents)}</td>
        <td>{formatCents(month.closingBalanceCents)}</td>
      </tr>)}
    </tbody>
  </table>
</div>;

const CashRunwayChart = ({
  months = [],
  showTable = true,
  poolLabels = {},
  eyebrow = "Pista de caixa",
  title = "Saldo projetado por grupo de caixa",
  description = "Os grupos são independentes e não se compensam entre si.",
}) => {
  const poolIds = [...new Set(months.flatMap((month) => (
    (month.closingBalancesByPoolingGroup || []).map((item) => item.poolingGroupId)
  )))].sort();
  const valuesFor = (poolId, field) => months.map((month) => (
    (month.closingBalancesByPoolingGroup || []).find((item) => item.poolingGroupId === poolId)?.[field] ?? null
  ));
  const datasets = poolIds.flatMap((poolId, index) => {
    const color = POOL_COLORS[index % POOL_COLORS.length];
    return [
      {
        label: `Caixa — ${poolLabels[poolId] || poolId}`,
        data: valuesFor(poolId, "balanceCents").map((value) => (
          Number.isSafeInteger(value) ? value / 100 : null
        )),
        borderColor: color,
        backgroundColor: color,
        borderWidth: 3,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.22,
        spanGaps: false,
      },
      {
        label: `${poolLabels[poolId] ? "Colchão" : "Buffer"} — ${poolLabels[poolId] || poolId}`,
        data: valuesFor(poolId, "minimumBufferCents").map((value) => (
          Number.isSafeInteger(value) ? value / 100 : null
        )),
        borderColor: color,
        borderDash: [7, 6],
        borderWidth: 1.5,
        pointRadius: 0,
      },
    ];
  });
  datasets.push({
    label: "Trilho zero",
    data: months.map(() => 0),
    borderColor: "#1C2030",
    borderDash: [3, 5],
    borderWidth: 1.5,
    pointRadius: 0,
  });
  const chartData = {
    labels: months.map((month) => formatCompetence(month.competence)),
    datasets,
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: "index" },
    plugins: {
      datalabels: { display: false },
      legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8 } },
      tooltip: { callbacks: { label: (context) => `${context.dataset.label}: ${formatCents(Math.round(context.parsed.y * 100))}` } },
    },
    scales: {
      x: { grid: { display: false } },
      y: { ticks: { callback: (value) => formatCents(Math.round(Number(value) * 100)) } },
    },
  };

  return <section className="director-runway" aria-labelledby="director-runway-title">
    <header>
      <div>
        <span className="director-eyebrow">{eyebrow}</span>
        <h2 id="director-runway-title">{title}</h2>
      </div>
      <p>{description}</p>
    </header>
    <div className="director-runway__canvas" aria-hidden="true">
      <Line data={chartData} options={options} />
    </div>
    {showTable && <CashRunwayTable months={months} />}
  </section>;
};

export default CashRunwayChart;
